import { useLingui } from '@lingui/react/macro';

import { Label } from '@/design_system/Label/Label';
import { RadioItem, RadioItemGroup } from '@/design_system/Radio/RadioItem/RadioItem';
import Stack from '@/design_system/Stack';
import { Column, Row, Table } from '@/design_system/Table/Table';
import Toggle from '@/design_system/Toggle';
import Paragraph from '@/design_system/Typography/Paragraph';
import { isExternalWorkshopUser, useUpdateUserSettings, useUserSettings } from '@/models/user';
import { useCurrentSession } from '@/services/auth';

type PriceDisplay = {
  workshopPrice: { showAmountBeforeTaxes: boolean; enabled: boolean };
  organizationPrice: { showAmountBeforeTaxes: boolean };
};

export const PriceDisplayPreferences = ({ showHeader = true }: { showHeader?: boolean }) => {
  const { t } = useLingui();

  const { mutate: updateUserSettings } = useUpdateUserSettings();
  const { currentSession } = useCurrentSession();
  const { data: userSettings } = useUserSettings(currentSession!.id);

  const priceDisplay = userSettings?.priceDisplay;

  const handleChangePriceDisplay = (
    price: 'workshopPrice' | 'organizationPrice',
    value: string
  ) => {
    if (price && value) {
      const updatedPriceDisplay = {
        ...priceDisplay,
        [price]: {
          ...priceDisplay?.[price],
          showAmountBeforeTaxes: value === 'excluded',
        },
      };

      updateUserSettings({
        userId: currentSession!.id,
        priceDisplay: updatedPriceDisplay as PriceDisplay,
      });
    }
  };

  const handleWorkshopPriceDisplayEnabled = (value: boolean) => {
    if (priceDisplay) {
      updateUserSettings({
        userId: currentSession!.id,
        priceDisplay: {
          ...priceDisplay,
          workshopPrice: {
            ...priceDisplay.workshopPrice,
            enabled: value,
          },
        },
      });
    }
  };

  if (!priceDisplay) return null;

  return (
    <Stack gap="1em">
      {showHeader && (
        <>
          <hr style={{ marginTop: '1.5em', paddingTop: '1em' }} />
          <Label
            label={t({ id: 'profile.preferences.label', message: 'Preferences' })}
            size="large"
          />
        </>
      )}
      <WorkshopPriceDisplay
        handleWorkshopPriceDisplayEnabled={handleWorkshopPriceDisplayEnabled}
        handleChangePriceDisplay={(value) => handleChangePriceDisplay('workshopPrice', value)}
        priceDisplay={priceDisplay}
      />

      <ClientPriceDisplay
        priceDisplay={priceDisplay}
        handleChangePriceDisplay={(value) => handleChangePriceDisplay('organizationPrice', value)}
      />
    </Stack>
  );
};

const WorkshopPriceDisplay = ({
  handleWorkshopPriceDisplayEnabled,
  handleChangePriceDisplay,
  priceDisplay,
}: {
  handleWorkshopPriceDisplayEnabled: (value: boolean) => void;
  handleChangePriceDisplay: (value: string) => void;
  priceDisplay: PriceDisplay;
}) => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();

  const showWorkshopPriceLabel = isExternalWorkshopUser(currentSession)
    ? t({ id: 'profile.preferences.showWorkshopPrice.workshop', message: 'Show price' })
    : t({ id: 'profile.preferences.showWorkshopPrice.internal', message: 'Show internal costs' });

  const showWorkshopPriceDescription = isExternalWorkshopUser(currentSession)
    ? t({
        id: 'profile.preferences.showWorkshopPrice.description.workshop',
        message: 'Display price on request and item pages.',
      })
    : t({
        id: 'profile.preferences.showWorkshopPrice.description.internal',
        message: 'Display internal cost on request and item pages.',
      });

  const priceDisplayLabel = isExternalWorkshopUser(currentSession)
    ? t({ id: 'profile.preferences.workshop.price', message: 'Price display' })
    : t({ id: 'profile.preferences.internal.cost.price', message: 'Internal cost display' });

  return (
    <Table columnWidths={['1fr', '1fr']} style={{ maxWidth: '600px' }} bordered>
      <Row>
        <Column>
          <Stack gap="0.5rem">
            <Paragraph weight="medium" size="100" align="start">
              {showWorkshopPriceLabel}
            </Paragraph>
            <Paragraph color="secondary" size="200" align="start">
              {showWorkshopPriceDescription}
            </Paragraph>
          </Stack>
        </Column>
        <Column justifyContent="flex-end">
          <Toggle
            isSelected={priceDisplay.workshopPrice.enabled}
            onChange={handleWorkshopPriceDisplayEnabled}
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <Paragraph weight="medium" size="100" align="start">
            {priceDisplayLabel}
          </Paragraph>
        </Column>
        <Column justifyContent="flex-end">
          <PriceDisplaySelector
            value={priceDisplay.workshopPrice.showAmountBeforeTaxes ? 'excluded' : 'included'}
            label={priceDisplayLabel}
            onChange={handleChangePriceDisplay}
          />
        </Column>
      </Row>
    </Table>
  );
};

const ClientPriceDisplay = ({
  priceDisplay,
  handleChangePriceDisplay,
}: {
  priceDisplay: PriceDisplay;
  handleChangePriceDisplay: (value: 'excluded' | 'included') => void;
}) => {
  const { t } = useLingui();
  const label = t({
    id: 'profile.preferences.organization.price',
    message: 'Client price display',
  });

  return (
    <Table columnWidths={['1fr', '1fr']} style={{ maxWidth: '600px' }} bordered>
      <Row>
        <Column>
          <Paragraph weight="medium" size="100" align="start">
            {label}
          </Paragraph>
        </Column>
        <Column justifyContent="flex-end">
          <PriceDisplaySelector
            value={priceDisplay.organizationPrice.showAmountBeforeTaxes ? 'excluded' : 'included'}
            label={label}
            onChange={handleChangePriceDisplay}
          />
        </Column>
      </Row>
    </Table>
  );
};

const PriceDisplaySelector = ({
  label,
  value,
  onChange,
}: {
  value: 'excluded' | 'included';
  onChange: (value: 'excluded' | 'included') => void;
  label: string;
}) => {
  const { t } = useLingui();

  const priceDisplayOptions = [
    {
      value: 'excluded',
      label: t({ id: 'profile.preferences.excluding.price', message: 'Excluding VAT' }),
    },
    {
      value: 'included',
      label: t({ id: 'profile.preferences.including.price', message: 'Including VAT' }),
    },
  ];

  return (
    <RadioItemGroup name={label} value={value} onChange={(e) => onChange(e)}>
      {priceDisplayOptions.map((option) => (
        <RadioItem key={option.value} value={option.value} ariaLabel={option.label}>
          {option.label}
        </RadioItem>
      ))}
    </RadioItemGroup>
  );
};
